import React, { useState } from 'react';

import { checkTime, getCity, getSlugFromUrl, toMoney } from '@/libs/app';
import { useRouter } from 'next/router';
import { TFunction } from 'next-i18next';
import { Product } from '@/features/products/shared';
import {
  AuctionStatus,
  DiscountStatus,
  PagePath,
  PriceSymbol,
  ProductStatus,
} from '@/layouts/shared';
import {
  Icon,
  Button,
  ButtonVariant,
  WalletIcon,
  MessageLightIcon,
  DiamondFilledIcon,
  Link,
} from '@/components';
import { CountDown } from './countdown';
import { AuctionModel } from '@/models/auction';
import { useMasterData, useProfile } from '@/contexts/profile';
import { DollarCircleIcon } from '@/components/icons/DollarCircle';

export type InfoPartialProps = {
  data: Product;
  t: TFunction;
  isAuth: boolean;
  onConsultClick: () => void;
  onDepositClick: () => void;
  isConsultLoading: boolean;
  isDeposited: boolean;
  onRemindClick: (value: boolean) => void;
  auction?: AuctionModel;
};

const InfoPartial = ({
  data,
  isAuth,
  t,
  onConsultClick,
  isDeposited,
  onDepositClick,
  isConsultLoading,
  onRemindClick,
  auction,
}: InfoPartialProps) => {
  const router = useRouter();
  const masterData = useMasterData();
  const ambassadorInfo = masterData?.ambassador_info;
  const profile: any = useProfile();

  const {
    price,
    token,
    is_auction: isAuction,
    is_partner: isPartner,
    id: productId,
    product_code: productCode,
    product_name_detail: productNameDetail,
    tags,
    status,
    product_discount,
    quotation,
    description,
    branch,
  } = data ?? {};

  console.log('data: ', data);

  const {
    color: color,
    gem_size: gemSize,
    gem_type: gemType,
    gem_pure: gemPure,
    gem_small: gemSmall,
    gem_weight: gemWeight,
    gem_material: gemMaterial,
    jewelry_material: jewelryMaterial,
    ni_number: niNumber,
    gem_master_size: gemMasterSize,
    inspection_certificate: inspectionCertificate,
  } = data?.attributes ?? {};

  const items = [
    { label: t('detail.label.gem-material'), value: gemMaterial },
    { label: t('detail.label.gem-master-size'), value: gemMasterSize },
    { label: t('detail.label.gem-type'), value: gemType },
    { label: t('detail.label.color'), value: color },
    { label: t('detail.label.gem-pure'), value: gemPure },
    { label: t('detail.label.gem-small'), value: gemSmall },
    { label: t('detail.label.jewelry-t-material'), value: jewelryMaterial },
    { label: t('detail.label.ni-number'), value: niNumber },
    { label: t('detail.label.gem-size'), value: gemSize },
    { label: t('detail.label.gem-weight'), value: gemWeight },
    { label: t('detail.label.inspection-certificate'), value: inspectionCertificate },
  ];
  let isTimeDiscount = false;
  if (product_discount) {
    isTimeDiscount =
      new Date(new Date().toISOString()).getTime() -
        new Date(new Date(product_discount?.ended_at).toISOString()).getTime() <
      0;
  }
  const [show, setShow] = useState<boolean>(true);
  const [checked, setChecked] = useState<boolean>(
    data.reminding_resell_product ? true : false
  );

  const showCountDown = () => {
    setShow(false);
  };
  const remindProduct = () => {
    setChecked(!checked);
    onRemindClick(!checked);
  };

  let isDiscount = product_discount
    ? checkTime(product_discount?.started_at, product_discount?.ended_at)
    : false;
  if (product_discount && product_discount?.status === DiscountStatus.Ended)
    isDiscount = false;

  const getProductCommission = () => {
    if (!profile?.is_ambassador) {
      return null;
    }

    const userLevel = profile?.level;
    const productConfigs = data?.ambassador_commission_configs;

    if (!userLevel || !productConfigs) {
      return null;
    }

    return productConfigs.find((item: any) => item.user_level === userLevel);
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-y-4 tw-overflow-hidden">
      {show && isDiscount && (
        <>
          <div className="tw-py-3 tw-px-4 tw-flex">
            <CountDown
              startTime={product_discount.started_at}
              endTime={product_discount.ended_at}
              showCountDown={showCountDown}
              t={t}
            />
            <div className="tw-ml-4 tw-mt-5 tw-text-secondary-70 tw-text-base tw-lowercase">
              {t('detail.label.time-discount')}
            </div>
          </div>
        </>
      )}{' '}
      <div className="tw-w-full md:tw-px-4">
        <div>
          <h1
            dangerouslySetInnerHTML={{ __html: productNameDetail }}
            className="tw-text-22 tw-line-clamp-2 tw-text-primary-main"
          />
          <h2
            dangerouslySetInnerHTML={{ __html: productNameDetail }}
            className="tw-hidden"
          />
          <h3
            dangerouslySetInnerHTML={{ __html: productNameDetail }}
            className="tw-hidden"
          />
          <span className="tw-mt-1 tw-text-secondary-70 tw-flex tw-items-center tw-gap-1">
            {productCode}
            {isPartner && (
              <>
                <span className="tw-w-[1px] tw-h-4 tw-bg-secondary-70"></span>
                <span>{getCity(branch)}</span>
              </>
            )}
          </span>
          {tags && !!tags.length && (
            <div className="tw-mt-1 tw-text-secondary-70">
              tags:{' '}
              {tags.map((tag, index) => (
                <span key={`tag-${index}`}>
                  <Link
                    url={{
                      pathname: PagePath.ProductsPage,
                      query: { tag: tag.tag_name },
                    }}
                    className="!tw-text-blue-50"
                  >
                    #{tag.tag_name}
                  </Link>
                  {index < tags.length - 1 ? ', ' : ''}
                </span>
              ))}
            </div>
          )}
        </div>
        {isAuction && auction && auction.status != AuctionStatus.Ended ? (
          <>
            <div className="tw-flex tw-flex-col tw-mb-6">
              <div className="tw-text-lg">Giá khởi điểm:</div>
              <div className="tw-flex tw-space-x-3 tw-items-center">
                <span className="tw-font-bold tw-text-warning-90">
                  {toMoney(auction.origin_price || 0, t)}
                </span>
                <span className="tw-font-bold">~</span>
                <span className="tw-font-bold tw-text-primary-main">
                  {toMoney(
                    (auction.origin_price || 0) / 100000,
                    t,
                    PriceSymbol.Atd
                  )}
                </span>
              </div>
              <div className="tw-text-lg tw-mt-3">Giá hiện tại:</div>
              <div className="tw-flex tw-space-x-3 tw-items-center">
                <span className="tw-font-bold tw-text-warning-90">
                  {toMoney(auction.sell_price || 0, t)}
                </span>
                <span className="tw-font-bold">~</span>
                <span className="tw-font-bold tw-text-primary-main">
                  {toMoney(
                    (auction.sell_price || 0) / 100000,
                    t,
                    PriceSymbol.Atd
                  )}
                </span>
              </div>
            </div>
          </>
        ) : (
          <>
            {!quotation && (
              <div className="tw-pt-4 tw-pb-6">
                <div className="tw-flex tw-gap-x-2 tw-leading-8 tw-text-26">
                  {show && isDiscount ? (
                    <>
                      {' '}
                      <span className="tw-font-bold tw-text-warning-90">
                        {toMoney(
                          price - (price * product_discount.discount) / 100,
                          t
                        )}
                      </span>
                      <span className="tw-font-bold">~</span>
                      <span className="tw-font-bold tw-text-primary-main">
                        {toMoney(
                          token - (token * product_discount.discount) / 100,
                          t,
                          PriceSymbol.Atd
                        )}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="tw-font-bold tw-text-warning-90">
                        {toMoney(price, t)}
                      </span>
                      <span className="tw-font-bold">~</span>
                      <span className="tw-font-bold tw-text-primary-main">
                        {toMoney(token, t, PriceSymbol.Atd)}
                      </span>
                    </>
                  )}
                </div>
                {isDiscount && (
                  <div className="tw-mt-1 tw-text-gray-60">
                    <span className="tw-line-through tw-text-lg">
                      {toMoney(price, t)} ~ {toMoney(token, t, PriceSymbol.Atd)}
                    </span>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <div className="tw-flex tw-flex-col tw-items-center sm:tw-flex-row tw-gap-y-4 tw-gap-x-0 sm:tw-gap-x-4 sm:tw-gap-y-0 tw-pb-8  !tw-w-full">
          {quotation ? (
            <div className="tw-w-full tw-flex tw-justify-start tw-mt-6">
              <Button
                disabled={status !== ProductStatus.Availability}
                onClick={onConsultClick}
                loading={isConsultLoading}
                className="tw-flex tw-items-center tw-justify-center !tw-w-44 !tw-rounded-5 sm:tw-w-1/2 tw-text-warning-90 tw-border-warning-90"
                variant={ButtonVariant.Outlined}
              >
                <div className="tw-flex tw-text-lg tw-gap-x-4 tw-justify-center tw-uppercase">
                  {t('detail.btn.contact')}
                </div>
              </Button>
            </div>
          ) : (
            <>
              <Button
                disabled={!isAuth || isDeposited}
                onClick={onConsultClick}
                loading={isConsultLoading}
                className="tw-flex tw-items-center tw-justify-center !tw-w-48 !tw-rounded-5 sm:tw-w-1/2"
                variant={ButtonVariant.Outlined}
              >
                <div className="tw-flex tw-text-lg tw-justify-center tw-uppercase">
                  <Icon className="tw-mr-4" icon={<MessageLightIcon />} />
                  {t('detail.btn.consult')}
                </div>
              </Button>
              {!isPartner && (
                <Button
                  disabled={!isAuth || isDeposited || isAuction || isPartner}
                  onClick={onDepositClick}
                  className="w-flex tw-items-center tw-justify-center !tw-w-48 !tw-rounded-5 sm:tw-w-1/2"
                >
                  <div className="tw-flex tw-gap-x-4 tw-justify-center tw-text-lg tw-uppercase">
                    <Icon icon={<WalletIcon />} />
                    <p>
                      {isDeposited
                        ? t('detail.btn.soldOut')
                        : t('detail.btn.deposit')}
                    </p>
                  </div>
                </Button>
              )}
            </>
          )}
        </div>
        {isAuth ? (
          <>
            {status != ProductStatus.Availability && (
              <div className="tw-flex tw-border-b tw-items-center tw-border-primary-main tw-border-opacity-50 tw-pb-6 tw-gap-x-5">
                <span className="tw-text-lg tw-flex-1 tw-italic">
                  Nhận thông báo khi sản phẩm được mở bán lại
                </span>
                <input
                  className="tw-mr-2 tw-mt-[0.3rem] tw-h-6 tw-w-10 tw-appearance-none tw-rounded-[3rem] tw-bg-[#D9D9D9] before:tw-pointer-events-none before:tw-absolute before:tw-h-3.5 before:tw-w-3.5 before:tw-rounded-full before:tw-bg-transparent before:tw=-content-[''] after:tw-absolute after:tw-z-[2] after:tw-mt-[0.125rem] after:tw-ml-[0.15rem] after:tw-h-5 after:tw-w-5 after:tw-rounded-full after:tw-border-none after:tw-bg-[#D9D9D9] after:tw-shadow-[0_0px_3px_0_rgb(0_0_0_/_7%),_0_2px_2px_0_rgb(0_0_0_/_4%)] after:tw-transition-[background-color_0.2s,transform_0.2s] after:tw-content-[''] checked:tw-bg-primary-main checked:after:tw-absolute checked:after:tw-z-[2] checked:after:tw-mt-[0.125rem] checked:after:tw-ml-[1.0625rem] checked:after:tw-h-5 checked:after:tw-w-5 checked:after:tw-rounded-full checked:after:tw-border-none checked:after:tw-bg-primary checked:after:tw-shadow-[0_4px_1px_-2px_rgba(0,0,0,0.2),_0_2px_2px_0_rgba(0,0,0,0.14),_0_1px_5px_0_rgba(0,0,0,0.12)] checked:after:tw-transition-[background-color_0.2s,transform_0.2s] checked:after:tw-content-[''] hover:tw-cursor-pointer focus:tw-outline-none focus:tw-ring-0 focus:before:tw-scale-100 focus:before:tw-opacity-0 focus:before:tw-shadow-[3px_-1px_0px_13px_rgba(0,0,0,0.6)] focus:before:tw-transition-[box-shadow_0.2s,transform_0.2s] focus:after:tw-absolute focus:after:tw-z-[1] focus:after:tw-block focus:after:tw-h-5 focus:after:tw-w-5 focus:after:tw-rounded-full focus:after:tw-content-[''] checked:focus:tw-border-primary-main checked:focus:tw-bg-primary-main checked:focus:before:tw-ml-[1.0625rem] checked:focus:before:tw-scale-100 checked:focus:before:tw-shadow-[3px_-1px_0px_13px_#3b71ca] checked:focus:before:tw-transition-[box-shadow_0.2s,transform_0.2s] dark:tw-bg-[#D9D9D9] dark:after:tw-bg-white dark:checked:tw-bg-primary dark:checked:after:tw-bg-primary dark:focus:before:tw-shadow-[3px_-1px_0px_13px_rgba(255,255,255,0.4)] dark:checked:focus:before:tw-shadow-[3px_-1px_0px_13px_#3b71ca]"
                  type="checkbox"
                  role="switch"
                  id="flexSwitchCheckDefault01"
                  checked={checked}
                  onChange={() => remindProduct()}
                />
              </div>
            )}
          </>
        ) : (
          <></>
        )}
        {data.ambassador_commission_configs &&
          data.ambassador_commission_configs.length > 0 && (
            <>
              {isAuth && profile?.is_ambassador ? (
                <>
                  {getProductCommission() && (
                    <div
                      className={`tw-pb-6 tw-text-primary-main tw-text-right tw-italic tw-border-b tw-border-primary-main`}
                    >
                      <div className="tw-flex tw-items-center tw-justify-end">
                        <DollarCircleIcon />
                        <span className="tw-ml-1">
                          {t('detail.label.auth-commission-label')}
                          {': '}
                          <strong>
                            {toMoney(
                              getProductCommission().commission_price,
                              t
                            )}
                          </strong>
                        </span>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div
                  className={`tw-pb-6 tw-text-primary-main tw-text-right tw-italic tw-border-b tw-border-primary-main`}
                >
                  <div className="tw-flex tw-items-center tw-justify-end">
                    <DollarCircleIcon />
                    <span className="tw-ml-1">
                      {ambassadorInfo?.title_commission}
                    </span>
                  </div>
                  <p className="tw-underline">
                    <Link
                      url={{
                        pathname: `/article/${getSlugFromUrl(
                          ambassadorInfo?.url ?? ''
                        )}`,
                      }}
                      target="_blank"
                    >
                      {t('detail.label.become-ambassador')}
                    </Link>
                  </p>
                </div>
              )}
            </>
          )}
        <div className="tw-border-t tw-border-primary-70"></div>
        <div className="tw-flex tw-gap-y-1 tw-flex-col tw-mt-6">
          {isPartner ? (
            <>
              <h3 className="tw-flex tw-items-center tw-text-primary-main">
                <Icon
                  icon={<DiamondFilledIcon />}
                  className="tw-w-5 tw-h-5 tw-opacity-50 tw-mr-2"
                />
                <span className="tw-text-xl">{t('detail.detail')}</span>
              </h3>
              <div className="tw-mt-3 tw-whitespace-pre-wrap tw-leading-8">
                {description}
              </div>
            </>
          ) : (
            <>
              <h3 className="tw-flex tw-items-center tw-text-primary-main">
                <Icon
                  icon={<DiamondFilledIcon />}
                  className="tw-w-5 tw-h-5 tw-opacity-50 tw-mr-2"
                />
                <span className="tw-text-xl">{t('detail.description')}</span>
              </h3>
              <div className="tw-mt-3">
                {items.map(({ value, label }, index) => {
                  if (value) {
                    return (
                      value && (
                        <div
                          className="tw-flex tw-pl-2 tw-items-start tw-text-lg"
                          key={`item-index-${index}`}
                        >
                          <div className="tw-flex tw-items-center">
                            <span className="tw-w-1 tw-h-1 tw-rounded-full tw-bg-black tw-mr-4"></span>
                            <span
                              className="tw-text-lg tw-text-secondary-70"
                              dangerouslySetInnerHTML={{ __html: label }}
                            />
                            :
                          </div>
                          <span
                            className="tw-flex-1 tw-font-bold tw-ml-3"
                            dangerouslySetInnerHTML={{ __html: value }}
                          />
                        </div>
                      )
                    );
                  }
                })}
              </div>
            </>
          )}
          {!isPartner && (
            <div className="tw-mt-3 tw-flex tw-flex-col tw-text-base tw-gap-2">
              {isAuth ? (
                <Link
                  url="/guide-to-measuring-hands/"
                  className="!tw-text-blue-50 tw-italic"
                  target="_blank"
                >
                  Hướng dẫn đo kích thước và quy đổi size
                </Link>
              ) : (
                <Link
                  url="/guide-to-measuring-hands/"
                  className="!tw-text-blue-50 tw-italic"
                  target="_blank"
                >
                  Hướng dẫn đo kích thước và quy đổi size
                </Link>
              )}
              <Link
                url={{ pathname: PagePath.ProductWarrantyPolicyPage }}
                className="!tw-text-blue-50 tw-italic"
                target="_blank"
              >
                Xem chính sách bảo hành sản phẩm
              </Link>
              <Link
                url={{ pathname: PagePath.RefundPolicyPage }}
                className="!tw-text-blue-50 tw-italic"
                target="_blank"
              >
                Xem chính sách thu đổi
              </Link>
              <Link
                url={{ pathname: PagePath.ConsignPolicyPage }}
                className="!tw-text-blue-50 tw-italic"
                target="_blank"
              >
                Xem chính sách mua bán/ký gửi sản phẩm
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InfoPartial;
